import { TFunction } from 'i18next';
import Joi from 'joi';
import { getLocalizedMessageOptions } from 'services/Localization/joiValidation';
import { areDifferent } from 'utils/array';
import Asset from './asset/asset';
import Classification from './classification/classification';
import { IOwner } from './owner';
import ProcessingActivity_Translation from './processingActivity_Translation';
import ResourceLink from './resourceLink';
import StringValue, { DataZoneKey } from './stringValue/stringValue';
import Tag from './tag';
import Task from './tasks/task';

export enum ProcessingActivityState {
  Concept = 0,
  Approved = 1,
  Archived = 2,
  AwaitingApproval = 3,
}

export enum ProcessingActivityRole {
  Controller = 0,
  Processor = 1,
}

export enum PrivacyFramework {
  GDPR = 0,
  SwissDSG = 1,
}

export enum ComplianceIssue {
  IsProcessorHasNoController = 0,
  IsProcessorHasNoPurposeDescription = 1,
  IsControllerHasNoRecipients = 2,
  IsControllerHasNoPurpose = 3,
  IsControllerHasNoLegalBasis = 4,
  IsControllerHasNoDataSubjects = 5,
  IsControllerHasNoData = 6,
  IsControllerHasNoRetentionPeriod = 7,
  MissingJointControllerAgreementLink = 8,
  MissingAdditionalSafeguardsLink = 9,
  NotAllDataHasRetentionPeriod = 10,
}

export default class ProcessingActivity implements IOwner {
  processingActivityId: number;

  code: string;

  ownerId?: string;

  ownerRoleId?: string;

  commentTrailId: number;

  auditTrailId: number;

  state: ProcessingActivityState;

  role: ProcessingActivityRole;

  trans: ProcessingActivity_Translation[];

  name: string;

  description: string;

  purpose: string;

  tasks: Task[];

  recipients: Asset[];

  recipientCategories: StringValue[];

  processors: Asset[];

  data: Asset[];

  joinControllers: Asset[];

  responsibleEntitiesTenants: string[];

  tagIds?: number[];

  created?: Date;

  startDate?: Date;

  endDate?: Date;

  approvalDate?: Date;

  taskId_DPIA: number | undefined;

  dpiaTask?: Task;

  authSchemaId?: number;

  transIdx: number;

  dataSubjects: StringValue[];

  dataCategories: StringValue[];

  legalBasis: StringValue[];

  executingEntities: StringValue[];

  retentionPeriod?: StringValue;

  dataSubjectScale?: StringValue;

  resourceLinks: ResourceLink[];

  // Not mapped
  newTags: Tag[];

  constructor() {
    this.processingActivityId = -1;
    this.commentTrailId = 0;
    this.auditTrailId = 0;
    this.state = ProcessingActivityState.Concept;
    this.role = ProcessingActivityRole.Controller;
    this.trans = [];
    this.code = '';
    this.name = '';
    this.description = '';
    this.purpose = '';
    this.tasks = [];
    this.recipients = [];
    this.recipientCategories = [];
    this.processors = [];
    this.data = [];
    this.joinControllers = [];
    this.responsibleEntitiesTenants = [];
    this.transIdx = -1;
    this.newTags = [];
    this.dataSubjects = [];
    this.dataCategories = [];
    this.legalBasis = [];
    this.executingEntities = [];
    this.resourceLinks = [];
  }

  static getStateText(state: ProcessingActivityState, t: TFunction<string[]>): string {
    switch (state) {
      case ProcessingActivityState.Concept:
        return t('processingActivity:State.Concept');
      case ProcessingActivityState.Approved:
        return t('processingActivity:State.Approved');
      case ProcessingActivityState.Archived:
        return t('processingActivity:State.Archived');
      case ProcessingActivityState.AwaitingApproval:
        return t('processingActivity:State.AwaitingApproval');
    }
  }

  public getStateText(t: TFunction<string[]>): string {
    return ProcessingActivity.getStateText(this.state, t);
  }

  static getRoleText(role: ProcessingActivityRole, t: TFunction<string[]>): string {
    switch (role) {
      case ProcessingActivityRole.Controller:
        return t('processingActivity:Role.Controller');
      case ProcessingActivityRole.Processor:
        return t('processingActivity:Role.Processor');
    }
  }

  public getRoleText(t: TFunction<string[]>): string {
    return ProcessingActivity.getRoleText(this.role, t);
  }

  public getDataZoneKey(): DataZoneKey {
    const dataZoneKey: number = Math.max(
      ...this.processors.map((a) => a.location?.stringValueKey || 0),
      ...this.recipients.map((a) => a.location?.stringValueKey || 0),
      ...this.data.map((a) => a.location?.stringValueKey || 0),
      1,
    );

    return dataZoneKey as DataZoneKey;
  }

  public getNumberOfTransfersText(t: TFunction<string[]>): string {
    const supplierTransfers =
      this.processors.filter((a) => a.location?.stringValueKey && a.location.stringValueKey !== 1).length +
      this.recipients.filter((a) => a.location?.stringValueKey && a.location.stringValueKey !== 1).length;
    const dataTransfers = this.data.filter((a) => a.location?.stringValueKey && a.location.stringValueKey !== 1).length;
    switch (true) {
      case supplierTransfers == 0 && dataTransfers == 0:
        return t('processingActivity:TabDetails.Fields.EEATransfer.No');
      case supplierTransfers > 0 && dataTransfers == 0:
        return t('processingActivity:TabDetails.Fields.EEATransfer.YesRecipients', { count: supplierTransfers });
      case supplierTransfers == 0 && dataTransfers > 0:
        return t('processingActivity:TabDetails.Fields.EEATransfer.YesData', { count: dataTransfers });
      default:
        return t('processingActivity:TabDetails.Fields.EEATransfer.YesBoth', { countSuppliers: supplierTransfers, countData: dataTransfers });
    }
  }

  public getRequiredSafeGuardsText(t: TFunction<string[]>, privacyFramework: PrivacyFramework): string {
    const dataZone: DataZoneKey = this.getDataZoneKey();
    switch (dataZone) {
      case DataZoneKey.TenantZone:
        return t('processingActivity:TabDetails.Fields.SafeGuards.EEA');
      case DataZoneKey.Adequate:
        return privacyFramework === PrivacyFramework.SwissDSG
          ? t('processingActivity:TabDetails.Fields.SafeGuards.DSGAdequate')
          : t('processingActivity:TabDetails.Fields.SafeGuards.EUAdequate');
      case DataZoneKey.Other:
        return t('processingActivity:TabDetails.Fields.SafeGuards.Other');
      default:
        return t('processingActivity:TabDetails.Fields.SafeGuards.EEA');
    }
  }

  public getTransfersText(t: TFunction<string[]>, privacyFramework: PrivacyFramework): string {
    return ProcessingActivity.getTransfersText(this.getDataZoneKey(), t, privacyFramework);
  }

  public static getTransfersText(
    dataZone: DataZoneKey,
    t: TFunction<string[]>,
    privacyFramework: PrivacyFramework,
  ): string {
    switch (dataZone) {
      case DataZoneKey.TenantZone:
        return t('processingActivities:TabOverview.Column.Transfers.EEA');
      case DataZoneKey.Adequate:
        return privacyFramework === PrivacyFramework.GDPR
          ? t('processingActivities:TabOverview.Column.Transfers.EUAdequate')
          : t('processingActivities:TabOverview.Column.Transfers.DSGAdequate');
      case DataZoneKey.Other:
        return t('processingActivities:TabOverview.Column.Transfers.Other');
      default:
        return t('processingActivities:TabOverview.Column.Transfers.EEA');
    }
  }

  public static getTransfersTextLabel(
    t: TFunction<string[]>,
    privacyFramework: PrivacyFramework,
    count?: number,
  ): string {
    switch (privacyFramework) {
      case PrivacyFramework.GDPR:
        return count !== undefined
          ? t('processingActivities:TabOverview.Column.Transfers.LabelEUCount', { count: count })
          : t('processingActivities:TabOverview.Column.Transfers.LabelEU');
      case PrivacyFramework.SwissDSG:
        return count !== undefined
          ? t('processingActivities:TabOverview.Column.Transfers.LabelSwissCount', { count: count })
          : t('processingActivities:TabOverview.Column.Transfers.LabelSwiss');
      default:
        return t('processingActivities:TabOverview.Column.Transfers.LabelEU');
    }
  }

  public getComplianceIssueText(
    issue: ComplianceIssue,
    t: TFunction<string[]>,
    privacyFramework: PrivacyFramework,
  ): string {
    if (privacyFramework === PrivacyFramework.SwissDSG) {
      switch (issue) {
        case ComplianceIssue.IsProcessorHasNoController:
          return t('processingActivity:ComplianceIssuesDSG.IsProcessorHasNoController');
        case ComplianceIssue.IsProcessorHasNoPurposeDescription:
          return t('processingActivity:ComplianceIssuesDSG.IsProcessorHasNoPurposeDescription');
        case ComplianceIssue.IsControllerHasNoRecipients:
          return t('processingActivity:ComplianceIssuesDSG.IsControllerHasNoRecipients');
        case ComplianceIssue.IsControllerHasNoPurpose:
          return t('processingActivity:ComplianceIssuesDSG.IsControllerHasNoPurpose');
        case ComplianceIssue.IsControllerHasNoLegalBasis:
          return '';
        case ComplianceIssue.IsControllerHasNoDataSubjects:
          return t('processingActivity:ComplianceIssuesDSG.IsControllerHasNoDataSubjects');
        case ComplianceIssue.IsControllerHasNoData:
          return t('processingActivity:ComplianceIssuesDSG.IsControllerHasNoData');
        case ComplianceIssue.IsControllerHasNoRetentionPeriod:
          return t('processingActivity:ComplianceIssuesDSG.IsControllerHasNoRetentionPeriod');
        case ComplianceIssue.MissingJointControllerAgreementLink:
          return '';
        case ComplianceIssue.MissingAdditionalSafeguardsLink:
          return t('processingActivity:ComplianceIssuesDSG.MissingAdditionalSafeguardsLink');
        case ComplianceIssue.NotAllDataHasRetentionPeriod:
          return t('processingActivity:ComplianceIssuesDSG.NotAllDataHasRetentionPeriod');
      }
    }

    switch (issue) {
      case ComplianceIssue.IsProcessorHasNoController:
        return t('processingActivity:ComplianceIssues.IsProcessorHasNoController');
      case ComplianceIssue.IsProcessorHasNoPurposeDescription:
        return t('processingActivity:ComplianceIssues.IsProcessorHasNoPurposeDescription');
      case ComplianceIssue.IsControllerHasNoRecipients:
        return t('processingActivity:ComplianceIssues.IsControllerHasNoRecipients');
      case ComplianceIssue.IsControllerHasNoPurpose:
        return t('processingActivity:ComplianceIssues.IsControllerHasNoPurpose');
      case ComplianceIssue.IsControllerHasNoLegalBasis:
        return t('processingActivity:ComplianceIssues.IsControllerHasNoLegalBasis');
      case ComplianceIssue.IsControllerHasNoDataSubjects:
        return t('processingActivity:ComplianceIssues.IsControllerHasNoDataSubjects');
      case ComplianceIssue.IsControllerHasNoData:
        return t('processingActivity:ComplianceIssues.IsControllerHasNoData');
      case ComplianceIssue.IsControllerHasNoRetentionPeriod:
        return t('processingActivity:ComplianceIssues.IsControllerHasNoRetentionPeriod');
      case ComplianceIssue.MissingJointControllerAgreementLink:
        return t('processingActivity:ComplianceIssues.MissingJointControllerAgreementLink');
      case ComplianceIssue.MissingAdditionalSafeguardsLink:
        return t('processingActivity:ComplianceIssues.MissingAdditionalSafeguardsLink');
      case ComplianceIssue.NotAllDataHasRetentionPeriod:
        return t('processingActivity:ComplianceIssues.NotAllDataHasRetentionPeriod');
    }
  }

  public getDataClassifications(): Classification[] {
    if (!this.data) return [];

    // Return the classification with the highest value for each classification group on any of the data Assets
    const classifications = this.data
      .flatMap((a) => a.classifications ?? [])
      .reduce((accumulator: Classification[], current) => {
        const currentHighestIndex = accumulator.findIndex((c) => c.group.stringValueId === current.group.stringValueId);
        if (currentHighestIndex === -1) {
          accumulator.push(current);
        } else if (current.value > accumulator[currentHighestIndex].value) {
          accumulator[currentHighestIndex] = current;
        }

        return accumulator;
      }, []);

    // Remove inherited status
    return classifications.map((c) => {
      const newClassification = c.clone();
      newClassification.inherited = false;

      return newClassification;
    });
  }

  clone(): ProcessingActivity {
    const newItem = new ProcessingActivity();
    newItem.processingActivityId = this.processingActivityId;
    newItem.code = this.code;
    newItem.ownerId = this.ownerId;
    newItem.ownerRoleId = this.ownerRoleId;
    newItem.commentTrailId = this.commentTrailId;
    newItem.auditTrailId = this.auditTrailId;
    newItem.state = this.state;
    newItem.role = this.role;
    newItem.trans = this.trans.map((t) => t.clone());
    newItem.name = this.name;
    newItem.description = this.description;
    newItem.purpose = this.purpose;
    newItem.tasks = this.tasks.map((t) => (t.clone ? t.clone() : t));
    newItem.dpiaTask = this.dpiaTask ? this.dpiaTask.clone() : undefined;
    newItem.recipients = this.recipients.map((a) => (a.clone ? a.clone() : a));
    newItem.recipientCategories = this.recipientCategories.map((a) => (a.clone ? a.clone() : a));
    newItem.processors = this.processors.map((a) => (a.clone ? a.clone() : a));
    newItem.data = this.data.map((a) => (a.clone ? a.clone() : a));
    newItem.joinControllers = this.joinControllers.map((a) => (a.clone ? a.clone() : a));
    newItem.responsibleEntitiesTenants = [...this.responsibleEntitiesTenants];
    newItem.tagIds = this.tagIds ? [...this.tagIds] : undefined;
    newItem.newTags = [...this.newTags];
    newItem.created = this.created;
    newItem.startDate = this.startDate;
    newItem.endDate = this.endDate;
    newItem.taskId_DPIA = this.taskId_DPIA;
    newItem.transIdx = this.transIdx;
    newItem.authSchemaId = this.authSchemaId;
    newItem.dataSubjects = this.dataSubjects.map((ds) => (ds.clone ? ds.clone() : ds));
    newItem.dataCategories = this.dataCategories.map((dc) => (dc.clone ? dc.clone() : dc));
    newItem.legalBasis = this.legalBasis.map((lb) => (lb.clone ? lb.clone() : lb));
    newItem.executingEntities = this.executingEntities.map((ee) => (ee.clone ? ee.clone() : ee));
    newItem.retentionPeriod = this.retentionPeriod ? this.retentionPeriod.clone() : undefined;
    newItem.dataSubjectScale = this.dataSubjectScale ? this.dataSubjectScale.clone() : undefined;
    newItem.resourceLinks = this.resourceLinks ? [...this.resourceLinks] : [];
    newItem.approvalDate = this.approvalDate;

    return newItem;
  }

  static getEmptyProcessingActivity(code: string): ProcessingActivity {
    const processingActivity = new ProcessingActivity();
    processingActivity.code = code;
    const trans = new ProcessingActivity_Translation();
    processingActivity.trans.push(trans);
    processingActivity.transIdx = 0;

    return processingActivity;
  }

  isEqual(item: ProcessingActivity): boolean {
    if (item.processingActivityId !== this.processingActivityId) return false;
    if (item.code !== this.code) return false;
    if (item.ownerId !== this.ownerId) return false;
    if (item.ownerRoleId !== this.ownerRoleId) return false;
    if (item.commentTrailId !== this.commentTrailId) return false;
    if (item.auditTrailId !== this.auditTrailId) return false;
    if (item.state !== this.state) return false;
    if (item.role !== this.role) return false;
    if (item.name !== this.name) return false;
    if (item.description !== this.description) return false;
    if (item.purpose !== this.purpose) return false;
    if (item.taskId_DPIA !== this.taskId_DPIA) return false;
    if (item.created !== this.created) return false;
    if (item.startDate !== this.startDate) return false;
    if (item.endDate !== this.endDate) return false;
    if (item.authSchemaId !== this.authSchemaId) return false;
    if (item.transIdx !== this.transIdx) return false;
    if (item.retentionPeriod?.stringValueId !== this.retentionPeriod?.stringValueId) return false;
    if (item.dataSubjectScale?.stringValueId !== this.dataSubjectScale?.stringValueId) return false;

    if (areDifferent(item.tagIds, this.tagIds, (a: number, b: number) => a === b)) {
      return false;
    }

    if (
      areDifferent(item.trans, this.trans, (a: ProcessingActivity_Translation, b: ProcessingActivity_Translation) => {
        return a.languageId === b.languageId && a.name === b.name && a.description === b.description;
      })
    ) {
      return false;
    }

    if (areDifferent(item.tasks, this.tasks, (a: Task, b: Task) => a.taskId === b.taskId)) {
      return false;
    }

    if (areDifferent(item.recipients, this.recipients, (a: Asset, b: Asset) => a.assetId === b.assetId)) {
      return false;
    }

    if (
      areDifferent(
        item.recipientCategories,
        this.recipientCategories,
        (a: StringValue, b: StringValue) => a.stringValueId === b.stringValueId,
      )
    ) {
      return false;
    }

    if (areDifferent(item.processors, this.processors, (a: Asset, b: Asset) => a.assetId === b.assetId)) {
      return false;
    }

    if (areDifferent(item.data, this.data, (a: Asset, b: Asset) => a.assetId === b.assetId)) {
      return false;
    }

    if (areDifferent(item.joinControllers, this.joinControllers, (a: Asset, b: Asset) => a.assetId === b.assetId)) {
      return false;
    }

    if (
      areDifferent(item.responsibleEntitiesTenants, this.responsibleEntitiesTenants, (a: string, b: string) => a === b)
    ) {
      return false;
    }

    if (
      areDifferent(item.newTags, this.newTags, (a: Tag, b: Tag) => {
        return a.tagId === b.tagId;
      }) === true
    ) {
      return false;
    }

    if (
      areDifferent(item.dataSubjects, this.dataSubjects, (a: StringValue, b: StringValue) => {
        return a.stringValueId === b.stringValueId;
      }) === true
    ) {
      return false;
    }

    if (
      areDifferent(item.dataCategories, this.dataCategories, (a: StringValue, b: StringValue) => {
        return a.stringValueId === b.stringValueId;
      }) === true
    ) {
      return false;
    }

    if (
      areDifferent(item.legalBasis, this.legalBasis, (a: StringValue, b: StringValue) => {
        return a.stringValueId === b.stringValueId;
      }) === true
    ) {
      return false;
    }

    if (
      areDifferent(item.executingEntities, this.executingEntities, (a: StringValue, b: StringValue) => {
        return a.stringValueId === b.stringValueId;
      }) === true
    ) {
      return false;
    }

    return true;
  }

  validate(localizedFields: Record<string, string>): Joi.ValidationResult {
    const schema: Joi.ObjectSchema = Joi.object({
      code: Joi.string().max(32).required().label(localizedFields['code']),
      name: Joi.string().max(512).required().label(localizedFields['name']),
    }).prefs(getLocalizedMessageOptions());

    return schema.validate({ name: this.name, code: this.code }, { abortEarly: false });
  }
}
